import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Grid,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
} from "@mui/material";
import { Button, Typography } from "Atoms";
import AttachmentManager from "Atoms/AttachmentManager";
import DeletePopUp from "Components/DeletePopUp";
import { useNavigate } from "react-router-dom";
import {
  cloneTestPlanAPI,
  createTestReportFormFromTestvisionAPI,
  createTestPlanFlowInTestvisionAPI,
  createTestPlanSpecInTestvisionAPI,
  getTestvisionEnvTrsAPI,
  getTestPlanByNodeIdAPI,
  updateTestPlanAPI,
} from "Zustand/services/projectsService";
import { deleteAttachmentsAPI } from "Zustand/services/attachmentService";
import { useBoundStore } from "Zustand/store/mainStore";
import { toast } from "react-toastify";
import { PDF_EXTS, getAttachmentChanges } from "Utils/helperFunctions";

type PlanStatus = 0 | 1 | 2; // 0=in progress, 1=approved, 2=rejected
const APPROVED_STATUS: PlanStatus = 1;
const TEST_PLAN_APPROVAL_SPECIFIER = "testplanapproval";
type TestvisionTrOption = {
  tr_id: number | string;
  tr_seq: string;
};

// Local attachment shape, matching the pattern already used in the
// sibling Attachments.tsx tab (no shared module for this exists yet).
type AttachmentAtom = {
  id: string;
  title: string;
  description: string;
  file?: File;
  url?: string;
  mimetype?: string;
  nodeid?: string;
  specifier?: string;
};

const showTestvisionResponseToast = (
  response: any,
  successMessage: string
) => {
  if (Number(response?.status) === 200) {
    toast.success(response?.message || successMessage);
    return true;
  }

  toast.error(response?.message || "TestVision action failed.");
  return false;
};

const showTestvisionErrorToast = (error: any, fallbackMessage: string) => {
  const responseData = error?.response?.data;
  const message = responseData?.message || error?.message || fallbackMessage;
  toast.error(message);
};

// const STATUS_LABEL: Record<PlanStatus, string> = {
//   0: "In progress",
//   1: "Approved",
//   2: "Rejected",
// };

export default function TestPlanDetail(props: any) {
  const nav = useNavigate();
  const testPlanNodeId = props.testplannodeid ?? ""; // required
  const projectNodeId = props?.projectnodeid ?? ""; // needed for cloning payload

  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState<any | null>(null); // { nodeid, phase, version, status, ... }
  const [status, setStatus] = useState<PlanStatus | "">("");
  const [creatingSpecInTestvision, setCreatingSpecInTestvision] =
    useState(false);
  const [flowDialogOpen, setFlowDialogOpen] = useState(false);
  const [trOptions, setTrOptions] = useState<TestvisionTrOption[]>([]);
  const [selectedTrId, setSelectedTrId] = useState("");
  const [flowName, setFlowName] = useState("");
  const [loadingTrOptions, setLoadingTrOptions] = useState(false);
  const [creatingFlowInTestvision, setCreatingFlowInTestvision] =
    useState(false);
  const [flowError, setFlowError] = useState("");
  const [reportFormDialogOpen, setReportFormDialogOpen] = useState(false);
  const [selectedReportFormTrId, setSelectedReportFormTrId] = useState("");
  const [creatingReportFormInTestvision, setCreatingReportFormInTestvision] =
    useState(false);
  const [reportFormError, setReportFormError] = useState("");

  // New Version dialog
  const [cloneOpen, setCloneOpen] = useState(false);
  const [phaseInput, setPhaseInput] = useState("");
  const [versionInput, setVersionInput] = useState("");
  const [creatingNewVersion, setCreatingNewVersion] = useState(false);

  // Approval-document gate on the Status dropdown
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalUploads, setApprovalUploads] = useState<AttachmentAtom[]>([]);
  const [uploadingApproval, setUploadingApproval] = useState(false);
  const [approvalDeleteTarget, setApprovalDeleteTarget] =
    useState<AttachmentAtom | null>(null);
  const [deletingApproval, setDeletingApproval] = useState(false);

  const { testvisionusername, uploadAttachments } = useBoundStore();

  const approvalDocuments: AttachmentAtom[] = Array.isArray(
    plan?.hasattachment
  )
    ? plan.hasattachment
        .filter(
          (item: any) =>
            (item?.attachment?.specifier ?? item?.specifier) ===
            TEST_PLAN_APPROVAL_SPECIFIER
        )
        .map((item: any) => {
          const attachment = item?.attachment ?? item;
          return {
            id: attachment?.file?.fileid ?? attachment?.nodeid ?? "",
            title:
              attachment?.title ||
              attachment?.file?.fileid?.split("/").pop() ||
              "untitled",
            description: attachment?.description || "",
            url: attachment?.file?.url,
            mimetype: attachment?.file?.mimetype,
            nodeid: attachment?.nodeid,
            specifier: attachment?.specifier || "",
          };
        })
    : [];

  const hasApprovalAttachment = approvalDocuments.length > 0;

  // Existing (already-uploaded) approval documents + not-yet-uploaded ones,
  // shown as a single manager instead of two separate "uploaded"/"upload" panels.
  const combinedApprovalAttachments: AttachmentAtom[] = [
    ...approvalDocuments,
    ...approvalUploads,
  ];

  /** Load (and reload, e.g. after an approval-document upload) the plan */
  const refreshPlan = useCallback(async () => {
    if (!testPlanNodeId) return;
    setLoading(true);
    try {
      const res = await getTestPlanByNodeIdAPI(testPlanNodeId);
      const testplan = res?.testplan ?? res; // handle both shapes
      setPlan(testplan);
      setStatus((testplan?.status ?? 0) as PlanStatus);
    } finally {
      setLoading(false);
    }
  }, [testPlanNodeId]);

  const handleApprovalAttachmentsChange = useCallback(
    async (updated: AttachmentAtom[]) => {
      const { toEdit, toDelete } = getAttachmentChanges(
        combinedApprovalAttachments,
        updated
      );

      // Existing items never carry a `file` (disableFileChangeForExisting
      // blocks replacing them), so this isolates newly staged uploads.
      setApprovalUploads(updated.filter((item) => item.file));

      if (toDelete.length > 0) {
        // Don't delete yet — the item stays visible (it's re-derived from
        // `approvalDocuments`, untouched above) until the confirm dialog
        // resolves.
        setApprovalDeleteTarget(toDelete[0] as AttachmentAtom);
      }

      if (toEdit.length > 0) {
        try {
          await uploadAttachments(testPlanNodeId, [], toEdit);
          toast.success("Approval document updated.");
          await refreshPlan();
        } catch (e: any) {
          console.error("Failed to update approval document", e);
          toast.error(e?.message || "Failed to update approval document.");
        }
      }
    },
    [combinedApprovalAttachments, uploadAttachments, testPlanNodeId, refreshPlan]
  );

  const handleConfirmApprovalDelete = useCallback(async () => {
    if (!approvalDeleteTarget?.nodeid) {
      setApprovalDeleteTarget(null);
      return;
    }

    setDeletingApproval(true);
    try {
      await deleteAttachmentsAPI([approvalDeleteTarget.nodeid]);
      toast.success("Approval document deleted.");
      setApprovalDeleteTarget(null);
      await refreshPlan();
    } catch (e: any) {
      console.error("Failed to delete approval document", e);
      toast.error(e?.message || "Failed to delete approval document.");
    } finally {
      setDeletingApproval(false);
    }
  }, [approvalDeleteTarget, refreshPlan]);

  /** Load plan on mount */
  useEffect(() => {
    refreshPlan();
  }, [refreshPlan]);

  const handleApprovalUpload = useCallback(async () => {
    const uploadable = approvalUploads.filter((attachment) => attachment.file);

    if (!uploadable.length) {
      toast.error("Please choose a document to upload.");
      return;
    }

    setUploadingApproval(true);
    try {
      // Approval documents are regular attachments. The specifier is what
      // the Status dropdown's Approved gate looks for.
      const uploadsWithSpecifier = uploadable.map((attachment) => ({
        ...attachment,
        specifier: TEST_PLAN_APPROVAL_SPECIFIER,
        file: Object.assign(attachment.file as File, {
          title: attachment.title,
          description: attachment.description,
          specifier: TEST_PLAN_APPROVAL_SPECIFIER,
        }),
      }));

      await uploadAttachments(testPlanNodeId, uploadsWithSpecifier, []);
      setApprovalUploads([]);
      toast.success("Approval document uploaded.");
      await refreshPlan();
    } catch (e: any) {
      console.error("Failed to upload approval document", e);
      toast.error(e?.message || "Failed to upload approval document.");
    } finally {
      setUploadingApproval(false);
    }
  }, [approvalUploads, uploadAttachments, testPlanNodeId, refreshPlan]);

  /** Status change → PUT update */
  const handleStatusChange = async (newStatus: PlanStatus) => {
    setStatus(newStatus);
    try {
      await updateTestPlanAPI({ nodeid: testPlanNodeId, status: newStatus });
      setPlan((p: any) => (p ? { ...p, status: newStatus } : p));
    } catch (e) {
      // revert on error
      setStatus(plan?.status ?? 0);
      console.error("Failed to update plan status", e);
    }
  };

  /** Gate the dropdown: Approved requires a testplanapproval attachment first */
  const handleStatusSelect = (newStatus: PlanStatus) => {
    if (newStatus === APPROVED_STATUS && !hasApprovalAttachment) {
      setApprovalDialogOpen(true);
      return;
    }
    handleStatusChange(newStatus);
  };

  /** Clone modal confirm */
  const handleCloneConfirm = async () => {
    if (creatingNewVersion) return;

    setCreatingNewVersion(true);
    try {
      await cloneTestPlanAPI(testPlanNodeId, {
        phase: phaseInput.trim(),
        version: versionInput.trim(),
        istestplanofnodeid: projectNodeId,
      });
      toast.success("New version created");
      nav("/project-detail", {
        state: {
          projectnodeid: projectNodeId,
          project: props?.project,
          plannodeid: null,
          hasPlan: false,
          initialTabIndex: 1,
        },
      });
    } catch (e) {
      console.error("Clone failed", e);
      toast.error("Failed to create new version");
    } finally {
      setCreatingNewVersion(false);
    }
  };

  // Create Test Report is currently hidden in the UI, so the direct creation
  // handler stays disabled until that action is needed again.
  // const handleClick = async () => {
  //   try {
  //     const resp = await createTestReport(testPlanNodeId);
  //     console.log("Report created:", resp);
  //     // toast.success("Report created");
  //   } catch (e) {
  //     // toast.error("Failed to create report");
  //   }
  // };

  const handleCreateSpecInTestvision = async () => {
    if (!testPlanNodeId || !testvisionusername.trim()) return;

    setCreatingSpecInTestvision(true);
    try {
      const resp = await createTestPlanSpecInTestvisionAPI(testPlanNodeId, {
        user: testvisionusername.trim(),
      });
      console.log("TestVision spec created:", resp);
      showTestvisionResponseToast(
        resp,
        "Spec created in TestVision successfully."
      );
    } catch (e) {
      console.error("Failed to create TestVision spec", e);
      showTestvisionErrorToast(e, "Failed to create spec in TestVision.");
    } finally {
      setCreatingSpecInTestvision(false);
    }
  };

  const normalizeTrOptions = (response: any): TestvisionTrOption[] => {
    const source =
      (Array.isArray(response) && response) ||
      response?.trs ||
      response?.testrequests ||
      response?.testRequests ||
      response?.data ||
      response?.items ||
      [];

    if (!Array.isArray(source)) return [];

    return source
      .map((item: any) => {
        const trId = item?.tr_id ?? item?.trid ?? item?.id;
        const trSeq = item?.tr_seq ?? item?.trseq ?? item?.name ?? item?.label;

        if (trId === undefined || trId === null || !trSeq) {
          return null;
        }

        return {
          tr_id: trId,
          tr_seq: String(trSeq),
        };
      })
      .filter(Boolean) as TestvisionTrOption[];
  };

  const loadTrOptions = async (mode?: string) => {
    setLoadingTrOptions(true);
    setFlowError("");
    setReportFormError("");
    try {
      const response = await getTestvisionEnvTrsAPI(mode);
      setTrOptions(normalizeTrOptions(response));
    } catch (e) {
      console.error("Failed to load TestVision TR list", e);
      setFlowError("Failed to load TR list.");
      setReportFormError("Failed to load TR list.");
    } finally {
      setLoadingTrOptions(false);
    }
  };

  const handleLoadAllTrs = () => {
    // Unscoped call (no `mode`) — replaces the dropdown with every TR,
    // not just the ones filtered for this dialog's usual "results" mode.
    setSelectedReportFormTrId("");
    loadTrOptions("");
  };

  const handleOpenFlowDialog = () => {
    setSelectedTrId("");
    setFlowName("");
    setFlowError("");
    setFlowDialogOpen(true);
    loadTrOptions("flow");
  };

  const handleCloseFlowDialog = () => {
    if (creatingFlowInTestvision) return;
    setFlowDialogOpen(false);
  };

  const handleCreateFlowInTestvision = async () => {
    if (!testPlanNodeId || !testvisionusername.trim() || !selectedTrId) return;

    setCreatingFlowInTestvision(true);
    setFlowError("");
    try {
      const resp = await createTestPlanFlowInTestvisionAPI(testPlanNodeId, {
        user: testvisionusername.trim(),
        trid: selectedTrId,
        name: flowName
      });
      console.log("TestVision flow created:", resp);
      const isSuccess = showTestvisionResponseToast(
        resp,
        "Flow created in TestVision successfully."
      );
      if (isSuccess) setFlowDialogOpen(false);
      else setFlowError(resp?.message || "Failed to create flow in TestVision.");
    } catch (e) {
      console.error("Failed to create TestVision flow", e);
      const message =
        (e as any)?.response?.data?.message ||
        "Failed to create flow in TestVision.";
      showTestvisionErrorToast(e, message);
      setFlowError(message);
    } finally {
      setCreatingFlowInTestvision(false);
    }
  };

  const handleOpenReportFormDialog = () => {
    setSelectedReportFormTrId("");
    setReportFormError("");
    setReportFormDialogOpen(true);
    loadTrOptions("results");
  };

  const handleCloseReportFormDialog = () => {
    if (creatingReportFormInTestvision) return;
    setReportFormDialogOpen(false);
  };

  const handleCreateReportFormFromTestvision = async () => {
    if (!selectedReportFormTrId) return;

    setCreatingReportFormInTestvision(true);
    setReportFormError("");
    try {
      const resp = await createTestReportFormFromTestvisionAPI({
        trid: selectedReportFormTrId,
      });
      console.log("TestVision test report form created:", resp);
      const isSuccess = showTestvisionResponseToast(
        resp,
        "Test report form created from TestVision successfully."
      );
      if (isSuccess) setReportFormDialogOpen(false);
      else {
        setReportFormError(
          resp?.message || "Failed to create test report form from TestVision."
        );
      }
    } catch (e) {
      console.error("Failed to create test report form from TestVision", e);
      const message =
        (e as any)?.response?.data?.message ||
        "Failed to create test report form from TestVision.";
      showTestvisionErrorToast(e, message);
      setReportFormError(message);
    } finally {
      setCreatingReportFormInTestvision(false);
    }
  };

  const phase = plan?.phase ?? "";
  const version = plan?.version ?? "";

  const planSummary = [plan?.name, phase, version]
    .map((part) => (part === null || part === undefined ? "" : String(part).trim()))
    .filter(Boolean)
    .join("-");
  const planTitle = planSummary || "Test Plan";

  return (
    <Box>
      <Paper sx={{ p: 3 }} elevation={3}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Tooltip title={planTitle}>
            <Typography
              variant="h5"
              noWrap
              sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {loading ? "Loading test plan…" : planTitle}
            </Typography>
          </Tooltip>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={3}>
          <Grid size={12}>
            <Paper variant="outlined" sx={{ p: 3, minHeight: 320 }}>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <Typography variant="subtitle2">Phase</Typography>
                  <Typography variant="body1">{phase || "—"}</Typography>
                </Grid>

                <Grid size={6}>
                  <Typography variant="subtitle2">Version</Typography>
                  <Typography variant="body1">{version || "—"}</Typography>
                </Grid>

                <Grid size={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="plan-status-label">Status</InputLabel>
                    <Select
                      labelId="plan-status-label"
                      label="Status"
                      value={status === "" ? "" : status}
                      onChange={(e) =>
                        handleStatusSelect(e.target.value as PlanStatus)
                      }
                    >
                      <MenuItem value={0}>In progress</MenuItem>
                      <MenuItem value={1}>Approved</MenuItem>
                      <MenuItem value={2}>Rejected</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box sx={{ mt: 4 }}>
                <Divider sx={{ mb: 2 }} />
          

                <AttachmentManager
                  _attachments={combinedApprovalAttachments}
                  onChange={handleApprovalAttachmentsChange}
                  disableFileChangeForExisting
                  defaultExpanded
                  allowedFileExtensions={PDF_EXTS}
                  cropFeature={false}
                  downloadAble
                  title="Approval documents"
                />

                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleApprovalUpload}
                    disabled={!approvalUploads.length || uploadingApproval}
                  >
                    {uploadingApproval ? (
                      <CircularProgress
                        size={20}
                        color="inherit"
                        sx={{ mr: 1 }}
                      />
                    ) : null}
                    Upload
                  </Button>
                </Box>

                <DeletePopUp
                  open={!!approvalDeleteTarget}
                  handleClose={() =>
                    !deletingApproval && setApprovalDeleteTarget(null)
                  }
                  handleDelete={handleConfirmApprovalDelete}
                />
              </Box>

              <Box sx={{ mt: 6 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1.5 }}
                >
                  Actions
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    flexWrap: "wrap",
                    gap: 1.5,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleCreateSpecInTestvision}
                    disabled={
                      creatingSpecInTestvision || !testvisionusername.trim()
                    }
                    sx={{ minWidth: 220, height: 42, whiteSpace: "nowrap" }}
                  >
                    {creatingSpecInTestvision ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      "Create spec in testvision"
                    )}
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={handleOpenFlowDialog}
                    disabled={!testvisionusername.trim()}
                    sx={{ minWidth: 220, height: 42, whiteSpace: "nowrap" }}
                  >
                    Create Flow in testvision
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={handleOpenReportFormDialog}
                    sx={{ minWidth: 310, height: 42, whiteSpace: "nowrap" }}
                  >
                    Create test report from testvision TR
                  </Button>

                  {/* Create Test Report is hidden for now because report creation
                  should move through the TestVision-specific flow. */}
                  {/* <Button
                    variant="outlined"
                    onClick={handleClick}
                    disabled={creatingTestReport}
                    sx={{ minWidth: 180, height: 42, whiteSpace: "nowrap" }}
                  >
                    {creatingTestReport ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      "Create Test Report"
                    )}
                  </Button> */}

                  <Button
                    variant="outlined"
                    onClick={() => setCloneOpen(true)}
                    disabled={creatingNewVersion}
                    sx={{ minWidth: 150, height: 42, whiteSpace: "nowrap" }}
                  >
                    New Version
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* ───────────────── Clone dialog ───────────────── */}
      <Dialog
        open={cloneOpen}
        onClose={() => {
          if (!creatingNewVersion) setCloneOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create new version</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Phase"
            fullWidth
            sx={{ mt: 1, mb: 2 }}
            value={phaseInput}
            onChange={(e) => setPhaseInput(e.target.value)}
          />
          <TextField
            label="Version"
            fullWidth
            value={versionInput}
            onChange={(e) => setVersionInput(e.target.value)}
            placeholder="e.g. 1.0.1"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={() => setCloneOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCloneConfirm}
            disabled={
              creatingNewVersion ||
              !phaseInput.trim() ||
              !versionInput.trim() ||
              !projectNodeId
            }
          >
            {creatingNewVersion ? (
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
            ) : null}
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={approvalDialogOpen}
        onClose={() => setApprovalDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Approval document required</DialogTitle>
        <DialogContent dividers>
          <Typography>Please upload approval document</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="contained"
            onClick={() => setApprovalDialogOpen(false)}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={flowDialogOpen}
        onClose={handleCloseFlowDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create Flow in testvision</DialogTitle>
        <DialogContent dividers>
          {flowError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {flowError}
            </Alert>
          ) : null}
          <TextField
            label="Flow Name"
            fullWidth
            value={flowName}
            onChange={(e) => setFlowName(e.target.value)}
          />
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel id="testvision-tr-label">TR</InputLabel>
            <Select
              labelId="testvision-tr-label"
              label="TR"
              value={selectedTrId}
              onChange={(event) => setSelectedTrId(String(event.target.value))}
              disabled={loadingTrOptions || creatingFlowInTestvision}
            >
              {trOptions.map((option) => (
                <MenuItem key={String(option.tr_id)} value={String(option.tr_id)}>
                  {option.tr_seq}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {loadingTrOptions ? (
            <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            onClick={handleCloseFlowDialog}
            disabled={creatingFlowInTestvision}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateFlowInTestvision}
            disabled={
              creatingFlowInTestvision ||
              loadingTrOptions ||
              !selectedTrId ||
              !testvisionusername.trim()
            }
          >
            {creatingFlowInTestvision ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Create"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reportFormDialogOpen}
        onClose={handleCloseReportFormDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create test report from testvision TR</DialogTitle>
        <DialogContent dividers>
          {reportFormError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {reportFormError}
            </Alert>
          ) : null}

          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel id="testvision-report-form-tr-label">TR</InputLabel>
            <Select
              labelId="testvision-report-form-tr-label"
              label="TR"
              value={selectedReportFormTrId}
              onChange={(event) =>
                setSelectedReportFormTrId(String(event.target.value))
              }
              disabled={loadingTrOptions || creatingReportFormInTestvision}
            >
              {trOptions.map((option) => (
                <MenuItem key={String(option.tr_id)} value={String(option.tr_id)}>
                  {option.tr_seq}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {loadingTrOptions ? (
            <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
          <Button
            variant="text"
            onClick={handleLoadAllTrs}
            disabled={loadingTrOptions || creatingReportFormInTestvision}
          >
            Load all TRs
          </Button>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleCloseReportFormDialog}
              disabled={creatingReportFormInTestvision}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateReportFormFromTestvision}
              disabled={
                creatingReportFormInTestvision ||
                loadingTrOptions ||
                !selectedReportFormTrId
              }
            >
              {creatingReportFormInTestvision ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Create"
              )}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
